/**
 * KF-03-T02 — Engine-core indicator catalog.
 *
 * A renderer-agnostic registry of indicator metadata.  This module MUST NOT
 * import React components or anything from `src/lib/**` or `src/demo/**`.
 * The React bindings (series components, tooltip entries) belong in the widget
 * layer or `vn-kline-engine-react`.
 *
 * The catalog is intentionally modelled after — and compatible with —
 * `src/lib/core/types/indicator-catalog.ts`, but lives in the engine package
 * so that kernel consumers can rely on it without the legacy app layer.
 */
// ── Registry ─────────────────────────────────────────────────────────────────
const _catalog = new Map();
/**
 * Register an indicator entry.  Overwrites any previously registered entry
 * with the same `id` (last-write wins; safe for hot-reload scenarios).
 */
export function registerIndicator(entry) {
    _catalog.set(entry.id, entry);
}
/**
 * Register multiple entries at once.
 */
export function registerIndicators(entries) {
    for (const entry of entries) {
        registerIndicator(entry);
    }
}
/**
 * Look up a catalog entry by indicator type id.
 * Returns `undefined` when no entry is registered for the given id.
 */
export function lookupIndicator(id) {
    return _catalog.get(id);
}
/**
 * Return a snapshot of all registered entries.
 */
export function listIndicators() {
    return [..._catalog.values()];
}
/**
 * Retrieve the default params defined in the catalog for a given indicator
 * type, or an empty object if none are registered.
 */
export function getDefaultIndicatorParams(indicatorType) {
    return _catalog.get(indicatorType)?.defaultParams ?? {};
}
/**
 * Return the merged params for a key-building call.
 *
 * If a param key exists in `overrides`, the override wins.
 * Params not present in `overrides` fall back to the catalog default.
 * This ensures the canonical key is built against a full, consistent param set.
 */
export function resolveIndicatorParams(indicatorType, overrides) {
    const defaults = getDefaultIndicatorParams(indicatorType);
    if (!overrides || Object.keys(overrides).length === 0) {
        return defaults;
    }
    return { ...defaults, ...overrides };
}
/**
 * Clear all registered entries.  Useful only in test environments.
 * @internal
 */
export function _clearCatalogForTests() {
    _catalog.clear();
}
// ── Built-in registrations ───────────────────────────────────────────────────
registerIndicators([
    { id: "Candlestick", category: "trend", panePolicy: "overlay", scalePolicy: "price", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "HollowCandle", category: "trend", panePolicy: "overlay", scalePolicy: "price", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "OHLC", category: "trend", panePolicy: "overlay", scalePolicy: "price", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "HeikinAshi", category: "trend", panePolicy: "overlay", scalePolicy: "price", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "Line", category: "trend", panePolicy: "overlay", scalePolicy: "price", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "Area", category: "trend", panePolicy: "overlay", scalePolicy: "price", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "Bar", category: "trend", panePolicy: "overlay", scalePolicy: "price", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    {
        id: "EMA",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.ema.period", type: "number", defaultValue: 20, min: 1, max: 500, step: 1 }],
        defaultParams: { period: 20 },
    },
    {
        id: "MA",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.ma.period", type: "number", defaultValue: 20, min: 1, max: 500, step: 1 }],
        defaultParams: { period: 20 },
    },
    {
        id: "BBI",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
    },
    {
        id: "BollingerBand",
        category: "volatility",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "band", fields: ["upper", "middle", "lower"] },
        inputSchema: [
            { key: "period", labelKey: "indicator.bollinger.period", type: "number", defaultValue: 20, min: 2, max: 200, step: 1 },
            { key: "stdDev", labelKey: "indicator.bollinger.stdDev", type: "number", defaultValue: 2, min: 0.5, max: 5, step: 0.5 },
        ],
        defaultParams: { period: 20, stdDev: 2 },
    },
    {
        id: "RSI",
        category: "momentum",
        panePolicy: "separate",
        scalePolicy: "normalized",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.rsi.period", type: "number", defaultValue: 14, min: 2, max: 200, step: 1 }],
        defaultParams: { period: 14 },
    },
    {
        id: "MACD",
        category: "momentum",
        panePolicy: "separate",
        scalePolicy: "normalized",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "macd", fields: ["macd", "signal", "divergence"] },
        inputSchema: [
            { key: "fast", labelKey: "indicator.macd.fast", type: "number", defaultValue: 12, min: 1, max: 200, step: 1 },
            { key: "slow", labelKey: "indicator.macd.slow", type: "number", defaultValue: 26, min: 1, max: 200, step: 1 },
            { key: "signal", labelKey: "indicator.macd.signal", type: "number", defaultValue: 9, min: 1, max: 100, step: 1 },
        ],
        defaultParams: { fast: 12, slow: 26, signal: 9 },
    },
    {
        id: "KDJ",
        category: "momentum",
        panePolicy: "separate",
        scalePolicy: "normalized",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "stochastic", fields: ["k", "d", "j"] },
        inputSchema: [
            { key: "period", labelKey: "indicator.kdj.period", type: "number", defaultValue: 9, min: 1, max: 100, step: 1 },
        ],
        defaultParams: { period: 9 },
    },
    { id: "CCI", category: "momentum", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" }, defaultParams: { period: 20 } },
    { id: "WR", category: "momentum", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" }, defaultParams: { period: 14 } },
    { id: "SAR", category: "trend", panePolicy: "overlay", scalePolicy: "price", repaintPolicy: "repaint-on-close", outputSchema: { type: "scalar" } },
    { id: "Volume", category: "volume", panePolicy: "separate", scalePolicy: "volume", repaintPolicy: "no-repaint", outputSchema: { type: "histogram" } },
    { id: "OBV", category: "volume", panePolicy: "separate", scalePolicy: "volume", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "VR", category: "volume", panePolicy: "separate", scalePolicy: "volume", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "CVDApprox", category: "orderflow", panePolicy: "separate", scalePolicy: "volume", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "CVDRealtime", category: "orderflow", panePolicy: "separate", scalePolicy: "volume", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "Whale", category: "orderflow", panePolicy: "overlay", scalePolicy: "volume", repaintPolicy: "no-repaint", outputSchema: { type: "histogram" }, defaultParams: { threshold: 50000 } },
    { id: "StrengthElder", category: "strength", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "histogram" } },
    { id: "StrengthRelative", category: "strength", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "DMI", category: "trend", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar", fields: ["plusDI", "minusDI", "adx"] } },
    { id: "BIAS", category: "momentum", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" }, defaultParams: { period: 6 } },
    { id: "BRAR", category: "momentum", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar", fields: ["ar", "br"] }, defaultParams: { period: 26 } },
    { id: "MTM", category: "momentum", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" }, defaultParams: { period: 12 } },
    { id: "EMV", category: "volume", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" }, defaultParams: { period: 14 } },
    { id: "AO", category: "momentum", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "histogram" } },
    { id: "ROC", category: "momentum", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" }, defaultParams: { period: 12 } },
    { id: "TRIX", category: "trend", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" }, defaultParams: { period: 12 } },
    { id: "DMA", category: "trend", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "PVT", category: "volume", panePolicy: "separate", scalePolicy: "volume", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" } },
    { id: "PSY", category: "momentum", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" }, defaultParams: { period: 12 } },
    { id: "CR", category: "strength", panePolicy: "separate", scalePolicy: "normalized", repaintPolicy: "no-repaint", outputSchema: { type: "scalar" }, defaultParams: { period: 26 } },
    // CE26 additions
    {
        id: "WMA",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.wma.period", type: "number", defaultValue: 9, min: 1, max: 500, step: 1 }],
        defaultParams: { period: 9 },
    },
    {
        id: "VWMA",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.vwma.period", type: "number", defaultValue: 20, min: 1, max: 500, step: 1 }],
        defaultParams: { period: 20 },
    },
    {
        id: "HMA",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.hma.period", type: "number", defaultValue: 9, min: 1, max: 500, step: 1 }],
        defaultParams: { period: 9 },
    },
    {
        id: "DEMA",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.dema.period", type: "number", defaultValue: 9, min: 1, max: 500, step: 1 }],
        defaultParams: { period: 9 },
    },
    {
        id: "TEMA",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.tema.period", type: "number", defaultValue: 9, min: 1, max: 500, step: 1 }],
        defaultParams: { period: 9 },
    },
    {
        id: "MARibbon",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        defaultParams: { type: "EMA", periods: [5, 10, 15, 20, 25, 30, 35, 40, 45, 50] },
    },
    {
        id: "Ichimoku",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "band", fields: ["tenkan", "kijun", "senkouA", "senkouB", "chikou"] },
        inputSchema: [
            { key: "tenkan", labelKey: "indicator.ichimoku.tenkan", type: "number", defaultValue: 9, min: 1, max: 200, step: 1 },
            { key: "kijun", labelKey: "indicator.ichimoku.kijun", type: "number", defaultValue: 26, min: 1, max: 200, step: 1 },
            { key: "senkouB", labelKey: "indicator.ichimoku.senkouB", type: "number", defaultValue: 52, min: 1, max: 300, step: 1 },
            { key: "displacement", labelKey: "indicator.ichimoku.displacement", type: "number", defaultValue: 26, min: 0, max: 100, step: 1 },
        ],
        defaultParams: { tenkan: 9, kijun: 26, senkouB: 52, displacement: 26 },
    },
    {
        id: "Supertrend",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "band", fields: ["supertrend", "direction", "upperBand", "lowerBand"] },
        inputSchema: [
            { key: "period", labelKey: "indicator.supertrend.period", type: "number", defaultValue: 10, min: 1, max: 100, step: 1 },
            { key: "multiplier", labelKey: "indicator.supertrend.multiplier", type: "number", defaultValue: 3, min: 1, max: 20, step: 1 },
        ],
        defaultParams: { period: 10, multiplier: 3 },
    },
    {
        id: "Keltner",
        category: "volatility",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "band", fields: ["upper", "middle", "lower"] },
        inputSchema: [
            { key: "period", labelKey: "indicator.keltner.period", type: "number", defaultValue: 20, min: 1, max: 200, step: 1 },
            { key: "atrPeriod", labelKey: "indicator.keltner.atrPeriod", type: "number", defaultValue: 10, min: 1, max: 100, step: 1 },
            { key: "multiplier", labelKey: "indicator.keltner.multiplier", type: "number", defaultValue: 2, min: 1, max: 10, step: 1 },
        ],
        defaultParams: { period: 20, atrPeriod: 10, multiplier: 2 },
    },
    {
        id: "Donchian",
        category: "volatility",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "band", fields: ["upper", "middle", "lower"] },
        inputSchema: [{ key: "period", labelKey: "indicator.donchian.period", type: "number", defaultValue: 20, min: 1, max: 200, step: 1 }],
        defaultParams: { period: 20 },
    },
    {
        id: "ChaikinVol",
        category: "volatility",
        panePolicy: "separate",
        scalePolicy: "percent",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.chaikinVol.period", type: "number", defaultValue: 10, min: 1, max: 100, step: 1 }],
        defaultParams: { period: 10 },
    },
    {
        id: "StdDev",
        category: "volatility",
        panePolicy: "separate",
        scalePolicy: "normalized",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.stdDev.period", type: "number", defaultValue: 20, min: 1, max: 200, step: 1 }],
        defaultParams: { period: 20 },
    },
    {
        id: "HV",
        category: "volatility",
        panePolicy: "separate",
        scalePolicy: "percent",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.hv.period", type: "number", defaultValue: 20, min: 1, max: 200, step: 1 }],
        defaultParams: { period: 20 },
    },
    {
        id: "CI",
        category: "volatility",
        panePolicy: "separate",
        scalePolicy: "normalized",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.ci.period", type: "number", defaultValue: 14, min: 1, max: 100, step: 1 }],
        defaultParams: { period: 14 },
    },
    {
        id: "CMF",
        category: "volume",
        panePolicy: "separate",
        scalePolicy: "normalized",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.cmf.period", type: "number", defaultValue: 20, min: 1, max: 200, step: 1 }],
        defaultParams: { period: 20 },
    },
    {
        id: "ChaikinOscillator",
        category: "volume",
        panePolicy: "separate",
        scalePolicy: "normalized",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [
            { key: "fast", labelKey: "settings.fastPeriod", type: "number", defaultValue: 3, min: 1, max: 100, step: 1 },
            { key: "slow", labelKey: "settings.slowPeriod", type: "number", defaultValue: 10, min: 1, max: 200, step: 1 },
        ],
        defaultParams: { fast: 3, slow: 10 },
    },
    {
        id: "MFI",
        category: "volume",
        panePolicy: "separate",
        scalePolicy: "percent",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [{ key: "period", labelKey: "indicator.mfi.period", type: "number", defaultValue: 14, min: 1, max: 200, step: 1 }],
        defaultParams: { period: 14 },
    },
    {
        id: "VolOsc",
        category: "volume",
        panePolicy: "separate",
        scalePolicy: "percent",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [
            { key: "shortPeriod", labelKey: "indicator.volOsc.shortPeriod", type: "number", defaultValue: 5, min: 1, max: 50, step: 1 },
            { key: "longPeriod", labelKey: "indicator.volOsc.longPeriod", type: "number", defaultValue: 20, min: 1, max: 100, step: 1 },
        ],
        defaultParams: { shortPeriod: 5, longPeriod: 20 },
    },
    {
        id: "VWAP",
        category: "volume",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "band", fields: ["vwap", "upper1", "lower1", "upper2", "lower2", "upper3", "lower3"] },
        inputSchema: [
            { key: "anchorType", labelKey: "indicator.vwap.anchorType", type: "number", defaultValue: 0, min: 0, max: 2 },
            { key: "showBands", labelKey: "indicator.vwap.showBands", type: "number", defaultValue: 1, min: 0, max: 1 },
            { key: "multiplier", labelKey: "indicator.vwap.multiplier", type: "number", defaultValue: 1, min: 1, max: 10, step: 1 },
        ],
        defaultParams: { anchorType: 0, showBands: 1, multiplier: 1 },
    },
    {
        id: "AnchoredVWAP",
        category: "volume",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "repaint-always",
        outputSchema: { type: "band", fields: ["vwap", "upper1", "lower1", "upper2", "lower2", "upper3", "lower3"] },
        inputSchema: [
            { key: "anchorTime", labelKey: "indicator.anchoredVwap.anchorTime", type: "number", defaultValue: 0 },
            { key: "showBands", labelKey: "indicator.anchoredVwap.showBands", type: "number", defaultValue: 1, min: 0, max: 1 },
            { key: "multiplier", labelKey: "indicator.anchoredVwap.multiplier", type: "number", defaultValue: 1, min: 1, max: 10, step: 1 },
        ],
        defaultParams: { anchorTime: 0, showBands: 1, multiplier: 1 },
    },
    {
        id: "Alligator",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "macd", fields: ["jaw", "teeth", "lips"] },
        inputSchema: [
            { key: "jawPeriod", labelKey: "indicator.alligator.jawPeriod", type: "number", defaultValue: 13, min: 1, max: 100, step: 1 },
            { key: "jawShift", labelKey: "indicator.alligator.jawShift", type: "number", defaultValue: 8, min: 0, max: 50, step: 1 },
            { key: "teethPeriod", labelKey: "indicator.alligator.teethPeriod", type: "number", defaultValue: 8, min: 1, max: 100, step: 1 },
            { key: "teethShift", labelKey: "indicator.alligator.teethShift", type: "number", defaultValue: 5, min: 0, max: 50, step: 1 },
            { key: "lipsPeriod", labelKey: "indicator.alligator.lipsPeriod", type: "number", defaultValue: 5, min: 1, max: 100, step: 1 },
            { key: "lipsShift", labelKey: "indicator.alligator.lipsShift", type: "number", defaultValue: 3, min: 0, max: 50, step: 1 },
        ],
        defaultParams: { jawPeriod: 13, jawShift: 8, teethPeriod: 8, teethShift: 5, lipsPeriod: 5, lipsShift: 3 },
    },
    {
        id: "Fractals",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "band", fields: ["bearish", "bullish"] },
        defaultParams: {},
    },
    {
        id: "Gator",
        category: "trend",
        panePolicy: "separate",
        scalePolicy: "normalized",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "macd", fields: ["upper", "lower"] },
        inputSchema: [
            { key: "jawPeriod", labelKey: "indicator.gator.jawPeriod", type: "number", defaultValue: 13, min: 1, max: 100, step: 1 },
            { key: "jawShift", labelKey: "indicator.gator.jawShift", type: "number", defaultValue: 8, min: 0, max: 50, step: 1 },
            { key: "teethPeriod", labelKey: "indicator.gator.teethPeriod", type: "number", defaultValue: 8, min: 1, max: 100, step: 1 },
            { key: "teethShift", labelKey: "indicator.gator.teethShift", type: "number", defaultValue: 5, min: 0, max: 50, step: 1 },
            { key: "lipsPeriod", labelKey: "indicator.gator.lipsPeriod", type: "number", defaultValue: 5, min: 1, max: 100, step: 1 },
            { key: "lipsShift", labelKey: "indicator.gator.lipsShift", type: "number", defaultValue: 3, min: 0, max: 50, step: 1 },
        ],
        defaultParams: { jawPeriod: 13, jawShift: 8, teethPeriod: 8, teethShift: 5, lipsPeriod: 5, lipsShift: 3 },
    },
    {
        id: "AC",
        category: "momentum",
        panePolicy: "separate",
        scalePolicy: "normalized",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        defaultParams: {},
    },
    {
        id: "PivotStandard",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "band", fields: ["pp", "r1", "s1", "r2", "s2", "r3", "s3"] },
        inputSchema: [
            { key: "pivotType", labelKey: "indicator.pivotStandard.pivotType", type: "number", defaultValue: 0, min: 0, max: 4 },
        ],
        defaultParams: { pivotType: 0 },
    },
    {
        id: "PivotHighLow",
        category: "trend",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "band", fields: ["pp", "r1", "s1", "r2", "s2", "r3", "s3"] },
        inputSchema: [
            { key: "lookback", labelKey: "indicator.pivotHighLow.lookback", type: "number", defaultValue: 10, min: 1, max: 200, step: 1 },
            { key: "pivotType", labelKey: "indicator.pivotHighLow.pivotType", type: "number", defaultValue: 0, min: 0, max: 1 },
        ],
        defaultParams: { lookback: 10, pivotType: 0 },
    },
    {
        id: "MarketProfile",
        category: "orderflow",
        panePolicy: "separate",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [
            { key: "tickSize", labelKey: "indicator.marketProfile.tickSize", type: "number", defaultValue: 0.01, min: 0.0001, max: 1000, step: 0.0001 },
            { key: "tpoPeriodMinutes", labelKey: "indicator.marketProfile.tpoPeriodMinutes", type: "number", defaultValue: 30, min: 5, max: 240, step: 5 },
        ],
        defaultParams: { tickSize: 0.01, tpoPeriodMinutes: 30 },
    },
    {
        id: "VolumeProfile",
        category: "volume",
        panePolicy: "overlay",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [
            { key: "priceTickSize", labelKey: "indicator.volumeProfile.priceTickSize", type: "number", defaultValue: 0.01, min: 0.0001, max: 1000, step: 0.0001 },
        ],
        defaultParams: { priceTickSize: 0.01 },
    },
    {
        id: "Footprint",
        category: "orderflow",
        panePolicy: "separate",
        scalePolicy: "price",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [
            { key: "barIntervalMs", labelKey: "indicator.footprint.barIntervalMs", type: "number", defaultValue: 60000, min: 1000, max: 86400000, step: 1000 },
            { key: "priceTickSize", labelKey: "indicator.footprint.priceTickSize", type: "number", defaultValue: 0.01, min: 0.0001, max: 1000, step: 0.0001 },
        ],
        defaultParams: { barIntervalMs: 60000, priceTickSize: 0.01 },
    },
    {
        id: "DeltaProfile",
        category: "orderflow",
        panePolicy: "separate",
        scalePolicy: "volume",
        repaintPolicy: "no-repaint",
        outputSchema: { type: "scalar" },
        inputSchema: [
            { key: "barIntervalMs", labelKey: "indicator.deltaProfile.barIntervalMs", type: "number", defaultValue: 60000, min: 1000, max: 86400000, step: 1000 },
            { key: "priceTickSize", labelKey: "indicator.deltaProfile.priceTickSize", type: "number", defaultValue: 0.01, min: 0.0001, max: 1000, step: 0.0001 },
            { key: "smaPeriod", labelKey: "indicator.deltaProfile.smaPeriod", type: "number", defaultValue: 14, min: 1, max: 200, step: 1 },
        ],
        defaultParams: { barIntervalMs: 60000, priceTickSize: 0.01, smaPeriod: 14 },
    },
]);
